import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  MessageSquare,
  LogOut,
  Menu,
  Shield,
  ChevronRight,
  ChevronDown,
  Home,
  FileText,
  Book,
  GraduationCap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AreaToggle from '../components/AreaToggle';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';

const adminMenuItems = [
  { path: '/admin', label: 'Dashboard', icon: Home },
  {
    label: 'Administration',
    icon: Shield,
    children: [
      { path: '/admin/users', label: 'User Management', icon: Users },
      { path: '/admin/support', label: 'Support Messages', icon: MessageSquare },
      { path: '/admin/minutes', label: 'Meeting Minutes', icon: FileText },
      { path: '/admin/degree-work', label: 'Degree Work', icon: Book },
      { path: '/admin/learning', label: 'Learning Material', icon: GraduationCap },
    ]
  },
];

const MenuItem = ({ item, isActive, isMobile, onClose }) => {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;

  // A menu item is active if its own path matches or if any of its children's paths match
  const isMenuExpanded = hasChildren && item.children.some(child => location.pathname === child.path);
  const [isOpen, setIsOpen] = useState(isMenuExpanded);

  // Auto-expand if a child becomes active (e.g., via direct navigation)
  React.useEffect(() => {
    if (isMenuExpanded) {
      setIsOpen(true);
    }
  }, [isMenuExpanded]);

  const Icon = item.icon;

  if (hasChildren) {
    return (
      <div className="flex flex-col">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center justify-between gap-3 px-4 py-3 transition-all duration-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 w-full outline-none`}
          data-testid={`admin-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          )}
        </button>
        {isOpen && (
          <div className="flex flex-col bg-gray-100/30 py-1">
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = location.pathname === child.path;
              return (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={isMobile ? onClose : undefined}
                  className={`flex items-center gap-3 pl-12 pr-4 py-2.5 text-sm transition-all duration-200 ${isChildActive
                    ? 'text-emerald-700 font-medium bg-emerald-50/50 border-r-4 border-emerald-500'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/50'
                    }`}
                  data-testid={`admin-menu-sub-${child.path.split('/').pop()}`}
                >
                  <ChildIcon className="h-4 w-4" />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={isMobile ? onClose : undefined}
      className={`flex items-center gap-3 px-4 py-3 transition-all duration-200 ${isActive
        ? 'border-l-4 border-emerald-500 bg-emerald-50/50 text-emerald-900 font-medium'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
        }`}
      data-testid={`admin-menu-${item.path.split('/').pop() || 'dashboard'}`}
    >
      <Icon className="h-5 w-5" />
      <span>{item.label}</span>
    </Link>
  );
};

const Sidebar = ({ isMobile = false, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className={`${isMobile ? 'flex flex-col h-full' : 'h-screen sticky top-0 bg-[#f8f9fa] border-r border-gray-200 hidden md:flex flex-col'}`}>
      {/* Header */}
      <div className="p-6 pb-4">
        <Link to="/members" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-4" data-testid="back-to-members">
          <ChevronRight className="h-3 w-3 rotate-180" />
          Back to Members Area
        </Link>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Admin Console</h2>
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
            Admin
          </Badge>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto" data-testid="admin-sidebar">
        {adminMenuItems.map((item) => (
          <MenuItem
            key={item.path || item.label}
            item={item}
            isActive={item.path ? isActive(item.path) : false}
            isMobile={isMobile}
            onClose={onClose}
          />
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-gray-200 bg-white/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Shield className="h-5 w-5 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate" data-testid="admin-display-name">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-amber-600 truncate">Administrator</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="admin-sidebar-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fcfcfc]">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  // Redirect if not admin
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#fcfcfc]">
      {/* Header */}
      <header className="bg-[#280a28] text-white" data-testid="admin-header">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="admin-mobile-menu-toggle">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#f8f9fa]">
                <Sidebar isMobile onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>

            {/* Logo/Title */}
            <div className="text-center flex-1 md:text-left">
              <Link to="/admin" className="inline-block" data-testid="admin-logo-link">
                <h1 className="font-heading text-lg md:text-xl font-semibold text-amber-400">
                  St David (University Lodge) No. 36
                </h1>
                <p className="text-xs md:text-sm text-gray-300 mt-0.5">
                  Admin Console
                </p>
              </Link>
            </div>

            {/* Area Toggle Button */}
            <div className="flex items-center gap-2">
              <AreaToggle />
              <div className="w-10 md:hidden" />
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
        {/* Sidebar - desktop only */}
        <Sidebar />

        {/* Content */}
        <main className="min-h-[calc(100vh-72px)]" data-testid="admin-main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
